import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';

import { FetchErrorName } from '../../api/errors/types';
import { PopupType } from '../../components/popup/types';
import { LoggerLogOnce } from '../../logger/Logger';
import { fetchAssignedTablesAction } from '../apiActions/fetchAssignedTablesAction';
import { fetchCasinoConfigAction } from '../apiActions/fetchCasinoConfigAction';
import { fetchEnvAction } from '../apiActions/fetchEnvAction';
import {
  fetchTodayHistoryAction,
  fetchOlderHistoryAction,
  fetchSingleHistoryAction,
} from '../apiActions/fetchHistoryAction';
import { fetchLiveSupportMessagesAction } from '../apiActions/fetchLiveSupportMessagesAction';
import { fetchPlayerConfigAction } from '../apiActions/fetchPlayerConfigAction';
import { fetchPlayerConfigSyncAction } from '../apiActions/fetchPlayerConfigSyncAction';
import { fetchRtpAction } from '../apiActions/fetchRtpAction';
import { fetchStatsConfigAction } from '../apiActions/fetchStatsConfigAction';
import { fetchStatsSettingsAction } from '../apiActions/fetchStatsSettingsAction';
import { fetchTableConfigAction } from '../apiActions/fetchTableConfigAction';
import { fetchTableLevelMessagesAction } from '../apiActions/fetchTableLevelMessagesAction';
import { fetchUserInfoAction } from '../apiActions/fetchUserInfoAction';
import { fetchVersionsAction } from '../apiActions/fetchVersionsAction';
import { CoreRootState } from '../CoreStore';
import { popupActions } from '../slices/popup/slice';

export const fetchErrorMiddleware = createListenerMiddleware();

/**
 * The purpose of this middleware is to react to all of the fetch errors and process them in a centralised and standardized way.
 */
fetchErrorMiddleware.startListening({
  matcher: isAnyOf(
    fetchAssignedTablesAction.rejected,
    fetchCasinoConfigAction.rejected,
    fetchEnvAction.rejected,
    fetchLiveSupportMessagesAction.rejected,
    fetchOlderHistoryAction.rejected,
    fetchPlayerConfigAction.rejected,
    fetchPlayerConfigSyncAction.rejected,
    fetchRtpAction.rejected,
    fetchSingleHistoryAction.rejected,
    fetchStatsConfigAction.rejected,
    fetchStatsSettingsAction.rejected,
    fetchTableConfigAction.rejected,
    fetchTableLevelMessagesAction.rejected,
    fetchTodayHistoryAction.rejected,
    fetchUserInfoAction.rejected,
    fetchVersionsAction.rejected
  ),
  effect: (action, { dispatch, getState }) => {
    const isSessionExpiredError = action.error.name === FetchErrorName.UnauthorizedError;
    const state = getState() as CoreRootState;
    const sessionExpirationPopupVisible = state.popup.currentPopup?.popupName === PopupType.SessionExpired;

    // If the request was essential, we should show a popup to the user
    // We should not needlessly dispatch the popup if it is already visible
    // Session expiration popup takes precedence over all other generic error popups
    // Any session expiration error should be critical and display the blocking popup
    if (isSessionExpiredError) {
      LoggerLogOnce.logFailureOnce('errors', 'session', action.type, Date.now(), 1, 'fetchErrorMiddleware', true);
      if (!sessionExpirationPopupVisible) {
        dispatch(popupActions.overwritePopup(PopupType.SessionExpired));
      }
    } else if (action.meta?.arg?.essential && !sessionExpirationPopupVisible) {
      dispatch(popupActions.pushPopup(PopupType.GenericError));
    }
  },
});
